import { Sparkles, RefreshCw, AlertTriangle, ShieldAlert, CheckCircle2, Ruler, Activity, Wrench } from "lucide-react";
import { useTranslation } from "react-i18next";
import IndustrialMetrics from "./IndustrialMetrics";
import { DiffBadge } from "./atoms";

export default function InsightsTab({ insights, loading, error, onGenerate, warnings, productionRisks }) {
  const { t } = useTranslation();
  if (!insights) {
    return (
      <div className="space-y-5" data-testid="insights-empty-wrap">
        {(productionRisks?.length || 0) > 0 && (
          <IndustrialMetrics metrics={{}} productionRisks={productionRisks}/>
        )}
        <div className="tech-card p-10 text-center" data-testid="insights-empty">
          <div className="w-14 h-14 mx-auto rounded-full bg-blue-500/10 border border-blue-500/30 flex items-center justify-center mb-4">
            <Sparkles size={26} className="text-blue-400"/>
          </div>
          <h3 className="font-display font-semibold text-xl text-slate-100 mb-2">{t("insightsTab.emptyTitle")}</h3>
          <p className="text-sm text-slate-400 max-w-xl mx-auto mb-5">
            {t("insightsTab.emptyBody")}
          </p>
          <button onClick={onGenerate} disabled={loading} className="btn-primary px-6 py-3 rounded-md flex items-center gap-2 mx-auto disabled:opacity-50" data-testid="generate-insights-btn">
            <Sparkles size={16} className={loading ? "animate-pulse" : ""}/> {loading ? t("insightsTab.analyzing") : t("insightsTab.generateBtn")}
          </button>
          {error && <div className="mt-4 text-sm text-red-400" data-testid="insights-error">{error}</div>}
          {warnings?.length > 0 && (
            <div className="mt-8 max-w-xl mx-auto text-left">
              <div className="label-tech text-amber-400 mb-2 flex items-center gap-2 justify-center"><AlertTriangle size={12}/> {t("insightsTab.warningsDetected")}</div>
              <ul className="text-sm text-amber-200/80 space-y-1">
                {warnings.map((w, i) => <li key={`w-${i}`}>• {w}</li>)}
              </ul>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="insights-loaded">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="label-tech flex items-center gap-2"><Sparkles size={12} className="text-blue-400"/> {t("insightsTab.generatedLabel")}</div>
        <button onClick={onGenerate} disabled={loading} className="text-xs px-3 py-1.5 rounded-md bg-slate-800 border border-slate-700 hover:border-blue-500 text-slate-300 flex items-center gap-2" data-testid="regenerate-insights-btn">
          <RefreshCw size={11} className={loading ? "animate-spin" : ""}/> {t("insightsTab.regenerate")}
        </button>
      </div>

      {insights.production_risks?.length > 0 && (
        <div className="space-y-2">
          <div className="label-tech flex items-center gap-2"><ShieldAlert size={12} className="text-red-400"/> {t("insightsTab.productionRisks")}</div>
          {insights.production_risks.map((r, i) => (
            <div key={`pr-${i}`} className={`tech-card p-4 border-l-4 ${
              r.severity === "alert" ? "border-l-red-500" : r.severity === "warn" ? "border-l-amber-500" : "border-l-blue-500"
            }`} data-testid={`risk-${i}`}>
              <div className="flex items-start gap-3">
                <AlertTriangle size={16} className={`flex-shrink-0 mt-1 ${r.severity === "alert" ? "text-red-400" : r.severity === "warn" ? "text-amber-400" : "text-blue-400"}`}/>
                <div className="flex-1">
                  <div className="font-display font-semibold text-slate-100">{r.title}</div>
                  <div className="text-sm text-slate-400 mt-1">{r.body}</div>
                  {r.mitigation && <div className="text-xs text-emerald-300 mt-2 flex items-start gap-1"><CheckCircle2 size={11} className="mt-0.5 flex-shrink-0"/> {r.mitigation}</div>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {insights.critical_tolerances?.length > 0 && (
          <div className="tech-card p-5">
            <div className="label-tech mb-3 flex items-center gap-2"><Ruler size={12} className="text-blue-400"/> {t("insightsTab.criticalTolerances")}</div>
            <div className="space-y-3">
              {insights.critical_tolerances.map((tol, i) => (
                <div key={`tol-${i}`} className="pb-3 border-b border-slate-800 last:border-0 last:pb-0" data-testid={`tol-${i}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="font-mono-data text-sm text-slate-100">{tol.feature}</div>
                    <DiffBadge level={tol.difficulty}/>
                  </div>
                  <div className="text-xs text-blue-300 mt-1 font-mono-data">{tol.tolerance}</div>
                  {tol.machine_suggestion && <div className="text-xs text-slate-400 mt-1">→ {tol.machine_suggestion}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {insights.vibration_risk && (
          <div className="tech-card p-5">
            <div className="label-tech mb-3 flex items-center gap-2"><Activity size={12} className="text-blue-400"/> {t("insightsTab.vibrationRisk")}</div>
            <div className="flex items-center gap-3 mb-3">
              <DiffBadge level={insights.vibration_risk.level}/>
              <span className="text-sm text-slate-300">{insights.vibration_risk.notes}</span>
            </div>
            {insights.vibration_risk.recommended_strategies?.length > 0 && (
              <ul className="space-y-1.5 text-xs text-slate-400">
                {insights.vibration_risk.recommended_strategies.map((s, i) => <li key={`vs-${i}`} className="flex gap-2"><span className="text-blue-400 flex-shrink-0">→</span>{s}</li>)}
              </ul>
            )}
          </div>
        )}

        {insights.clamping_strategy && (insights.clamping_strategy.primary || insights.clamping_strategy.secondary) && (
          <div className="tech-card p-5">
            <div className="label-tech mb-3 flex items-center gap-2"><Wrench size={12} className="text-blue-400"/> {t("insightsTab.clampingStrategy")}</div>
            <div className="space-y-2 text-sm">
              {insights.clamping_strategy.primary && (
                <div><div className="text-xs text-slate-500 uppercase tracking-wide">{t("insightsTab.primary")}</div><div className="text-slate-200">{insights.clamping_strategy.primary}</div></div>
              )}
              {insights.clamping_strategy.secondary && (
                <div className="mt-2"><div className="text-xs text-slate-500 uppercase tracking-wide">{t("insightsTab.alternative")}</div><div className="text-slate-300">{insights.clamping_strategy.secondary}</div></div>
              )}
            </div>
            {insights.clamping_strategy.warnings?.length > 0 && (
              <ul className="mt-3 space-y-1 text-xs text-amber-200/80">
                {insights.clamping_strategy.warnings.map((w, i) => <li key={`cw-${i}`}>⚠ {w}</li>)}
              </ul>
            )}
          </div>
        )}

        {insights.surface_finish_notes && (
          <div className="tech-card p-5">
            <div className="label-tech mb-3 flex items-center gap-2"><Sparkles size={12} className="text-blue-400"/> {t("insightsTab.surfaceFinish")}</div>
            <p className="text-sm text-slate-300 leading-relaxed">{insights.surface_finish_notes}</p>
          </div>
        )}
      </div>

      {insights.inspection_plan?.length > 0 && (
        <div className="tech-card overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-700/60 label-tech flex items-center gap-2"><CheckCircle2 size={12} className="text-emerald-400"/> {t("insightsTab.inspectionPlan")}</div>
          <div className="divide-y divide-slate-800">
            {insights.inspection_plan.map((p, i) => (
              <div key={`ip-${i}`} className="px-5 py-3 grid grid-cols-12 gap-3 items-center" data-testid={`insp-${i}`}>
                <div className="col-span-12 md:col-span-4 text-sm text-slate-100">{p.feature}</div>
                <div className="col-span-7 md:col-span-5 text-xs text-slate-400">{p.method}</div>
                <div className="col-span-5 md:col-span-3 text-right">
                  <span className="text-[10px] label-tech px-2 py-1 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-300">{p.frequency}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
